import isEqual from 'lodash/isEqual';
import { createWithEqualityFn } from 'zustand/traditional';

/**
 * Per-conversation chat-model selection (Cursor-style). The value is the stable catalog
 * `model_id` from `GET /llm/models`, sent as `llm_model_id` (preferred) with the model's
 * cost `id` as `llm_model_cost_id` (rollout fallback, omitted when the model is unpriced).
 * A legacy stored cost id still resolves on read and is re-stored as a model_id on the next
 * pick. Kept client-side only — selection is scoped to the session, not persisted server-side.
 */
export type SelectedModelStore = {
  /** Map of conversationId → selected model_id (llm_model_id). */
  byConversation: Record<string, string>;
  setModel: (conversationId: string, modelId: string) => void;
  clear: (conversationId: string) => void;
};

export const useSelectedModelStore = createWithEqualityFn<SelectedModelStore>(
  (set, get) => ({
    byConversation: {},

    setModel: (conversationId, modelId) => {
      set({ byConversation: { ...get().byConversation, [conversationId]: modelId } });
    },

    clear: (conversationId) => {
      const next = { ...get().byConversation };
      delete next[conversationId];
      set({ byConversation: next });
    },
  }),
  isEqual,
);

export function useSelectedModel(conversationId: string): string | undefined {
  return useSelectedModelStore((s) => s.byConversation[conversationId]);
}
